'use client';

import React, { useEffect, useMemo } from 'react';

import {
    Box,
    BoxContent,
    Button,
    ButtonList,
    ButtonVariant,
    IconName,
    Modal,
    ModalBody,
    ModalFooter,
    Text,
    TextLevels,
    TrilogyColor,
    TypographyBold,
} from '@bytel/trilogy-react';

import { Odr } from '@app-types/odr';

import {
    sendOdrDetailModalOpenTracking,
    sendOdrDownloadTracking,
    sendOdrExternalLinkTracking,
} from '@odr-wall/helpers';
import { format } from 'date-fns';

interface OdrDetailModalProps {
    selectedOdr: Odr | undefined;
    onClose: () => void;
}

export function OdrDetailModal({ selectedOdr, onClose }: Readonly<OdrDetailModalProps>) {
    const formatDate = (dateString: Date): string => {
        return format(dateString, 'dd/MM/yyyy');
    };

    const isExpired = useMemo(() => selectedOdr?.isExpired ?? false, [selectedOdr]);

    useEffect(() => {
        if (selectedOdr) {
            const availableCtas = [selectedOdr.link && 'Web', selectedOdr.odrFile && 'Coupon'].filter(
                Boolean,
            ) as string[];

            sendOdrDetailModalOpenTracking(selectedOdr.title, availableCtas);
        }
    }, [selectedOdr]);

    const handleDownload = (url: string) => {
        sendOdrDownloadTracking();
        window.open(url, '_blank');
    };

    const handleExternalLink = (url: string) => {
        sendOdrExternalLinkTracking();
        window.open(url, '_blank');
    };

    if (!selectedOdr) {
        return null;
    }

    return (
        <Modal title='Offre de remboursement' onClose={onClose} active>
            <ModalBody>
                <Text level={TextLevels.ONE} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                    {selectedOdr.title}
                </Text>
                <Box shadowless>
                    <BoxContent backgroundColor={TrilogyColor.NEUTRAL_FADE}>
                        <Text level={TextLevels.TWO}>
                            Date d&apos;achat&nbsp;:{' '}
                            <b>
                                du {formatDate(selectedOdr.startDate)} au {formatDate(selectedOdr.endDate)}
                            </b>
                        </Text>
                        <Text level={TextLevels.TWO}>
                            Date limite de participation&nbsp;: <b>{formatDate(selectedOdr.lastDate)}</b>
                        </Text>
                    </BoxContent>
                </Box>
                {selectedOdr?.odrFile && !isExpired && (
                    <Button
                        className='link button-clear'
                        onClick={() => handleDownload(selectedOdr.odrFile!)}
                        iconName={IconName.DOWNLOAD}
                    >
                        Télécharger le coupon
                    </Button>
                )}
                {selectedOdr.description && <p dangerouslySetInnerHTML={{ __html: selectedOdr.description }} />}
            </ModalBody>

            {!isExpired && (
                <ModalFooter>
                    <ButtonList>
                        <Button variant={ButtonVariant.SECONDARY} onClick={onClose}>
                            Fermer
                        </Button>
                        {selectedOdr?.link ? (
                            <Button
                                variant={ButtonVariant.PRIMARY}
                                onClick={() => handleExternalLink(selectedOdr.link!)}
                            >
                                Faire la demande en ligne
                            </Button>
                        ) : undefined}
                    </ButtonList>
                </ModalFooter>
            )}
        </Modal>
    );
}
